import { quizSetSchema, type MediaAssetDescriptor, type QuizSet } from "@quizshow/protocol";

export interface ExportedMedia {
  descriptor: MediaAssetDescriptor;
  base64: string;
}

export interface QuizshowStudioDocument {
  format: "quizshow-studio";
  version: 1;
  exportedAt: string;
  quiz: QuizSet;
  media: ExportedMedia[];
}

export function createQuizDocument(
  quiz: QuizSet,
  media: ExportedMedia[],
  exportedAt = new Date().toISOString(),
): QuizshowStudioDocument {
  return { format: "quizshow-studio", version: 1, exportedAt, quiz, media };
}

export function parseQuizDocument(value: unknown): QuizshowStudioDocument {
  const document = value as Partial<QuizshowStudioDocument>;
  if (document.format !== "quizshow-studio" || document.version !== 1) {
    throw new Error("Kein unterstütztes Quizshow-Studio-Dokument.");
  }
  const parsedQuiz = quizSetSchema.safeParse(document.quiz);
  if (!parsedQuiz.success) {
    throw new Error(parsedQuiz.error.issues[0]?.message ?? "Quizshow-Studio-Datei ist ungültig.");
  }
  if (!Array.isArray(document.media)) {
    throw new Error("Die Medienliste der Quizshow-Studio-Datei fehlt.");
  }
  const expected = new Map(parsedQuiz.data.mediaAssets.map((asset) => [asset.mediaId, asset]));
  for (const entry of document.media) {
    if (!entry?.descriptor || typeof entry.base64 !== "string") {
      throw new Error("Ein Medium im Import ist unvollständig.");
    }
    const descriptor = expected.get(entry.descriptor.mediaId);
    if (
      !descriptor ||
      descriptor.bytes !== entry.descriptor.bytes ||
      descriptor.mimeType !== entry.descriptor.mimeType
    ) {
      throw new Error(`Medium "${entry.descriptor.fileName}" passt nicht zur Quizdefinition.`);
    }
  }
  if (document.media.length !== expected.size) {
    throw new Error("Die Quizshow-Studio-Datei enthält nicht alle erwarteten Medien.");
  }
  return {
    format: "quizshow-studio",
    version: 1,
    exportedAt: typeof document.exportedAt === "string" ? document.exportedAt : new Date(0).toISOString(),
    quiz: parsedQuiz.data,
    media: document.media,
  };
}
